import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useFormik } from 'formik';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown } from 'primereact/dropdown';
import { classNames } from 'primereact/utils';
import appActions from '../../../appActions';
import { dateTimeTemplate, fileGroupOptions, fileTypeOptions, statusOptions, yesNoOptions } from '../../../utils/mics';
import storage from '../../../utils/storage';
import DownloadFile from '../../../components/DownloadFile';
import { Toast } from 'primereact/toast';

const IntFileDetail = () => {

    const toast = useRef(null)

    const { state } = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const intfile = useSelector((state) => state.intFile.intfile);

    const [record, setRecord] = useState({});
    const [isEditMode, setIsEditMode] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);
    const [fileText, setFileText] = useState('')
    const [outputFileText, setOutputFileText ] = useState('')

    useEffect(() => {
        if (!state) return;
        const { data, isView, isEdit } = state;
        if (data && data.filePath) {
            getFileData(data.filePath, setFileText)
        }
        if (data && data.outfilePath) {
            getFileData(data.outfilePath, setOutputFileText)
        }
        setRecord(data || {});
        setIsViewMode(isView || false);
        setIsEditMode(isEdit || false);
    }, [state]);

    const handleSubmit = (data) => {
        if (data.id) {
            dispatch(appActions.INTFILE_UPDATE_REQUEST(data));
        } else {
            dispatch(appActions.INTFILE_CREATE_REQUEST(data));
        }
      
    };

    const handleRedirect = useCallback(() => {
        dispatch(appActions.INTFILE_RESET_REQUEST());
        navigate('/ui/integration-file', { replace: true });
    }, [dispatch, navigate]);

    useEffect(() => {
        if (intfile?.action === 'create' || intfile?.action === 'update') {
            handleRedirect();
        }
    }, [intfile, handleRedirect]);

    const getFileData = (filePath, callBackFunc) => {
        const URL = window && window.config && window.config.END_POINT;
        const token = storage.getToken()
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: `${URL}/integration/file/read?filePath=${filePath}`,
            headers: { 
                'Authorization': token
            }
        };
        
        axios.request(config).then((response) => {
            if (response.status === 200) {
                callBackFunc(JSON.stringify(response.data))
            }else {
                callBackFunc('')
            }
        })
        .catch((error) => {
            callBackFunc('')
        });
    }

    const formik = useFormik({
        initialValues: {
            id: record?.id || '',
            storeId: record?.storeId || '',
            fileName: record?.fileName || '',
            filePath: record?.filePath || '',
            fileGroup: record?.fileGroup || null,
            fileType: record?.fileType || null,
            received: record?.received || null,
            receivedOn: dateTimeTemplate(record?.receivedOn) || '',
            converted: record?.converted || null,
            convertedOn: dateTimeTemplate(record?.convertedOn) || '',
            outfilePath: record?.outfilePath || '',
            uploaded: record?.uploaded || null,
            uploadedOn: dateTimeTemplate(record?.uploadedOn) || '',
            uploadStatus: record?.uploadStatus || '',
            uploadMethod: record?.uploadMethod || '',
            uploadTime: record?.uploadTime || '',
            error: record?.error || null,
            fileStage: record?.fileStage || '',
            processed: record?.processed || null,
            lastUpdatedBy: record?.lastUpdatedBy || '',
            createdOn: dateTimeTemplate(record?.createdOn) || '',
            lastUpdated: dateTimeTemplate(record?.lastUpdated) || '',
            status: record && record.status ? statusOptions.find( status => status.code === record.status) : statusOptions.find((status) => status.code === 'Active'),
            validatedFile: record?.validatedFile || null,
            validatedFields: record?.validatedFields || null,
            reprocessed: record?.reprocessed || null,
            processedCount: record?.processedCount || '',
            errorLog: record?.errorLog || '',
            uploadCount: record?.uploadCount || '',
            uploadErrorCount: record?.uploadErrorCount || '',
            deleteCount: record?.deleteCount || '',
            deleteErrorCount: record?.deleteErrorCount || '',
            validateUploading: record?.validateUploading || null
        },
        enableReinitialize: true,
        validate: (data) => {
            let errors = {};
            if (!data.fileName) {
                errors.fileName = 'File Name is required.';
            }
            if (!data.fileGroup) {
                errors.fileGroup = 'File Group is required.';
            }
            return errors;
        },
        onSubmit: (values) => {
            const data = {
                ...values,
                status: values.status.code ? values.status.code : '',
            };
            if (isEditMode) {
                data['id'] = record.id;
            }
            handleSubmit(data);
        },
    });

    const isFormFieldValid = (name) =>
        !!(formik.touched[name] && formik.errors[name]);

    const getFormErrorMessage = (name) => {
        return (
            isFormFieldValid(name) && (
                <small className="p-error">{formik.errors[name]}</small>
            )
        );
    };

    const renderFormArea = () => {
        return (
            <div className="p-fluid formgrid grid br-2 bg-white p-2 py-4 border-round-xl shadow-1">

                {(isEditMode || isViewMode) && (
                    <div className="col-4 mb-3">
                        <label htmlFor="id">Id</label>
                        <InputText id="id" name="id" value={formik.values.id} onChange={formik.handleChange} disabled />
                    </div>
                )}

                <div className="col-4 mb-3">
                    <label htmlFor="storeId">Store Id</label>
                    <InputText id="storeId" name="storeId" value={formik.values.storeId} onChange={formik.handleChange} />
                </div>

                <div className="col-4 mb-3">
                    <label htmlFor="fileName">File Name*</label>
                    <InputText id="fileName" name="fileName" value={formik.values.fileName} onChange={formik.handleChange}
                        className={classNames({ 'p-invalid': isFormFieldValid('fileName') })} readOnly={isViewMode} />
                    {getFormErrorMessage('fileName')}
                </div>

                <div className="col-4 mb-2">
                    <label htmlFor="fileGroup">File Group*</label>
                    <Dropdown filter showClear value={formik.values.fileGroup} onChange={formik.handleChange} options={fileGroupOptions}  id="fileGroup" name="fileGroup" optionLabel="name" optionValue='name' className={classNames({ 'p-invalid': isFormFieldValid('fileGroup') })} disabled={isViewMode} />
                    {getFormErrorMessage('fileGroup')}
                </div>

                <div className="col-4 mb-3">
                    <label htmlFor="fileType">File Type</label>
                    <Dropdown filter showClear value={formik.values.fileType} onChange={formik.handleChange} options={fileTypeOptions}  id="fileType" name="fileType" optionLabel="name" optionValue='code' disabled={isViewMode} />
                </div>
                <div className="col-4 mb-3">
                    <label htmlFor="received">Received</label>
                    <Dropdown filter showClear value={formik.values.received} onChange={formik.handleChange} options={yesNoOptions} id="received" name="received" optionLabel="name" optionValue='code' disabled={isViewMode} />
                </div>
                <div className="col-4 mb-3">
                    <label htmlFor="receivedOn">Received On</label>
                    <InputText id="receivedOn" name="receivedOn" value={formik.values.receivedOn} onChange={formik.handleChange} readOnly={isViewMode} />
                </div>
                <div className="col-4 mb-3">
                    <label htmlFor="converted">Converted</label>
                    <Dropdown filter showClear value={formik.values.converted} onChange={formik.handleChange} options={yesNoOptions}  id="converted" name="converted" optionLabel="name" optionValue='code' disabled={isViewMode} />
                </div>
                <div className="col-4 mb-3">
                    <label htmlFor="convertedOn">Converted On</label>
                    <InputText id="convertedOn" name="convertedOn" value={formik.values.convertedOn} onChange={formik.handleChange} readOnly={isViewMode} />
                </div>
                <div className="col-4 mb-3">
                    <label htmlFor="uploaded">Uploaded</label>
                    <Dropdown filter showClear id="uploaded" name="uploaded" value={formik.values.uploaded} onChange={formik.handleChange} optionLabel="name" optionValue='code' options={yesNoOptions} disabled={isViewMode} />
                </div>
                <div className="col-4 mb-3">
                    <label htmlFor="uploadedOn">Uploaded On</label>
                    <InputText id="uploadedOn" name="uploadedOn" value={formik.values.uploadedOn} onChange={formik.handleChange} readOnly={isViewMode} />
                </div>
                <div className="col-4 mb-3">
                    <label htmlFor="uploadStatus">Upload Status</label>
                    <InputText id="uploadStatus" name="uploadStatus" value={formik.values.uploadStatus} onChange={formik.handleChange} readOnly={isViewMode} />
                </div>
                <div className="col-4 mb-3">
                    <label htmlFor="uploadMethod">Upload Method</label>
                    <InputText id="uploadMethod" name="uploadMethod" value={formik.values.uploadMethod} onChange={formik.handleChange} readOnly={isViewMode} />
                </div>
                <div className="col-4 mb-3">
                    <label htmlFor="uploadTime">Upload Time</label>
                    <InputText id="uploadTime" name="uploadTime" value={formik.values.uploadTime} onChange={formik.handleChange} readOnly={isViewMode} />
                </div>
                <div className="col-4 mb-3">
                    <label htmlFor="error">Error</label>
                    <Dropdown filter showClear id="error" name="error" value={formik.values.error} onChange={formik.handleChange} optionLabel="name" optionValue='code' options={yesNoOptions} disabled={isViewMode} />
                </div>
                <div className="col-4 mb-3">
                    <label htmlFor="fileStage">File Stage</label>
                    <InputText id="fileStage" name="fileStage" value={formik.values.fileStage} onChange={formik.handleChange} readOnly={isViewMode} />
                </div>
                <div className="col-4 mb-3">
                    <label htmlFor="processed">Processed</label>
                     <Dropdown filter showClear  id="processed" name="processed" value={formik.values.processed} onChange={formik.handleChange} optionLabel="name" optionValue='code' options={yesNoOptions} disabled={isViewMode} />
                </div>

                <div className="col-4 mb-3">
                    <label htmlFor="validatedFile">Validated File</label>
                    <Dropdown filter showClear  value={formik.values.validatedFile} onChange={formik.handleChange}  options={yesNoOptions}  id="validatedFile" name="validatedFile" optionLabel="name" optionValue='code' disabled={isViewMode} />
                </div>

                <div className="col-4 mb-3">
                    <label htmlFor="validatedFields">Validated Fields</label>
                    <Dropdown filter showClear  value={formik.values.validatedFields} onChange={formik.handleChange} options={yesNoOptions} id="validatedFields" name="validatedFields" optionLabel="name" optionValue='code' disabled={isViewMode} />
                </div>

                <div className="col-4 mb-3">
                    <label htmlFor="reprocessed">Reprocessed</label>
                    <Dropdown filter showClear  value={formik.values.reprocessed} onChange={formik.handleChange} options={yesNoOptions}  id="reprocessed" name="reprocessed" optionLabel="name" optionValue='code' disabled={isViewMode} />
                </div>

                <div className="col-4 mb-3">
                    <label htmlFor="processedCount">Processed Count</label>
                    <InputText id="processedCount" name="processedCount" value={formik.values.processedCount} onChange={formik.handleChange} readOnly={isViewMode} />
                </div>

                <div className="col-4 mb-3">
                    <label htmlFor="uploadCount"> Upload Count </label>
                    <InputText id="uploadCount" name="uploadCount" value={formik.values.uploadCount} onChange={formik.handleChange} />
                </div>

                <div className="col-4 mb-3">
                    <label htmlFor="uploadErrorCount"> Upload Error Count </label>
                    <InputText id="uploadErrorCount" name="uploadErrorCount" value={formik.values.uploadErrorCount} onChange={formik.handleChange} />
                </div>

                <div className="col-4 mb-3">
                    <label htmlFor="deleteCount"> Delete Count </label>
                    <InputText id="deleteCount" name="deleteCount" value={formik.values.deleteCount} onChange={formik.handleChange} />
                </div>

                <div className="col-4 mb-3">
                    <label htmlFor="deleteErrorCount"> Delete Error Count </label>
                    <InputText id="deleteErrorCount" name="deleteErrorCount" value={formik.values.deleteErrorCount} onChange={formik.handleChange} />
                </div>

                <div className="col-4 mb-3">
                    <label htmlFor="repvalidateUploadingrocessed"> Validate Uploading </label>
                    <Dropdown filter showClear  value={formik.values.validateUploading} onChange={formik.handleChange} options={yesNoOptions} 
                        id="validateUploading" name="validateUploading" optionLabel="name" optionValue='code' disabled={isViewMode} />
                </div>

                <div className="col-12 mb-3">
                    <label htmlFor="errorLog"> Error Log </label>
                    <InputTextarea autoResize  id="errorLog" name="errorLog" rows={2} value={formik.values.errorLog} onChange={formik.handleChange} readOnly />
                </div> 
              
                <div className="col-12 mb-2">
                    <label htmlFor="filePath">File Path { isViewMode && <DownloadFile filePath={formik.values.filePath} toast={toast}/>}</label>
                    <InputTextarea autoResize  id="filePath" name="filePath" rows={1} value={formik.values.filePath} onChange={formik.handleChange} readOnly={isViewMode} />
                </div>

                {(isEditMode || isViewMode) && (
                    <div className="col-12 mb-2">
                        <label htmlFor="pjofilePath"> File Text </label>
                        <InputTextarea autoResize  id="fileText" name="fileText" rows={5} value={fileText} onChange={formik.handleChange} readOnly />
                    </div> 
                )}
                
                <div className="col-12 mb-3">
                    <label htmlFor="outfilePath"> Outfile Path {isViewMode && <DownloadFile filePath={formik.values.outfilePath} toast={toast}/> }</label>
                    <InputTextarea autoResize  id="outfilePath" name="outfilePath" rows={1} value={formik.values.outfilePath} onChange={formik.handleChange} readOnly={isViewMode} />
                </div>

                {(isEditMode || isViewMode) && (
                    <div className="col-12 mb-2">
                        <label htmlFor="pjofilePath"> Output File Text </label>
                        <InputTextarea autoResize  id="outputFileText" name="outputFileText" rows={5} cols={30} value={outputFileText} onChange={formik.handleChange} readOnly />
                    </div> 
                )}

                
                {(isEditMode || isViewMode) && (
                    <>
                        <div className="col-4 mb-3">
                            <label htmlFor="createdOn">Created On</label>
                            <InputText id="createdOn" name="createdOn" value={formik.values.createdOn} onChange={formik.handleChange} disabled/>
                        </div>
                        <div className="col-4 mb-3">
                            <label htmlFor="lastUpdated">Last Updated</label>
                            <InputText id="lastUpdated" name="lastUpdated" value={formik.values.lastUpdated} onChange={formik.handleChange} disabled />
                        </div>
                        <div className="col-4 mb-3">
                            <label htmlFor="lastUpdatedBy">Last Updated By</label>
                            <InputText id="lastUpdatedBy" name="lastUpdatedBy" value={formik.values.lastUpdatedBy} onChange={formik.handleChange} disabled />
                        </div>
                    </>
                )}

               
            </div>
        );
    };

    const renderMainButton = () => {
        return (
            <>
                {!isViewMode &&
                    <Button label={isEditMode ? 'Update' : 'Save'} 
                    data-pr-tooltip={isEditMode ? 'Update' : 'Save'}
                    severity="success" outlined className="p-button-success tp"type="submit" />
                }
                <Button 
                    label={isViewMode ? 'Back' : 'Cancel'}
                     data-pr-tooltip={isViewMode ? 'Back' : 'Cancel'}
                    severity="secondary" outlined
                    icon={isViewMode ? 'pi pi-arrow-left' : 'pi pi-times'}
                    className="p-button-secondary tp" type="button"
                    onClick={() => navigate('/ui/integration-file')}
                />
            </>
        );
    };

    return (
        <>
            <Toast ref={toast} />
            <form onSubmit={formik.handleSubmit}>
                <div className="form_height px-2 mt-2">
                    <div className="d-flex flex-row gap-10 align-items-center mb-1">
                        <h2 className="page-header mr-auto"> {isEditMode ? 'Edit Integration File' : 'Integration File Details'} : {formik?.values?.fileName} </h2>
                        {renderMainButton()}
                    </div>
                    <div className="grid px-2">
                        <div className="col-9">{renderFormArea()}</div>
                        <div className="col-3 px-3">
                            <div className="p-fluid formgrid grid br-2 bg-white py-4 border-round-xl shadow-1">
                                <div className="col-12 mb-2">
                                    <label htmlFor="status">Status</label>
                                    <Dropdown id="status" name="status" value={formik.values.status} onChange={formik.handleChange} options={statusOptions} optionLabel="name" placeholder="Select" disabled={isViewMode} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </>
    );
};

export default IntFileDetail;
